import { FieldState } from '../../ap/fields/field';
import { DisplayValue } from '../../ap/fields/selectField';
import { createGuid } from '../../ap/utils/utils';
import * as validation from '../../ap/fields/validation/validation';
import { i18n } from '../../config/i18n';
import moment from 'moment';
import * as voiValidators from './voiValidators';
import {observable, action, makeObservable} from 'mobx';
import {Validator} from "../../ap/fields/validation/validation";

/**
 * The default values for the inputs
 */
let defaultValues = {
  firstName: '',
  middleNames: '',
  surname: '',
  addressLookup: '',
  addressLine1: '',
  addressLine2: '',
  suburb: '',
  state: { display: i18n('selectState'), value: '' },
  postcode: '',
  dpid: '',
  dob: '',
  email: '',
  phoneNumber: '',
  pinCode: '',
  propertyBeingTransacted: '',
  transactionStatus: '',
  categorySelectField: ''
}


/**
 * State for the VOI
 */
export class VOIState {
  /**
   * Main id
   */
  //Consider removing transactionId as this concept is not
  // longer required in the new solution.
  @observable transactionId?: string = undefined;

  @observable orderId?: string = undefined;
  securepayCustomerId?: string = undefined;
  securepayAccessToken?: string = undefined;

  /**
   * Name
   */
  @observable firstName?: FieldState<string> = undefined;
  @observable middleNames?: FieldState<string> = undefined;
  @observable surname?: FieldState<string> = undefined;

  /**
   * Address
   */
  @observable addressLookup?: FieldState<string> = undefined;
  @observable addressLine1?: FieldState<string> = undefined;
  @observable addressLine2?: FieldState<string> = undefined;
  @observable suburb?: FieldState<string> = undefined;
  @observable state?: FieldState<DisplayValue> = undefined;
  @observable postcode?: FieldState<string> = undefined;
  @observable dpid?: FieldState<string> = undefined;
  @observable searchTerm: string = '';
  @observable isManualAddressEntry: boolean = false;
  @observable useClientAddressAsTransacted: boolean = false;
  @observable fullClientAddress: string;

  /**
   * Some general others for EV
   */
  @observable dob?: FieldState<string> = undefined;
  @observable email?: FieldState<string> = undefined;

  /**
   * Phone verification
   */
  @observable phoneNumber?: FieldState<string> = undefined;
  @observable pinCode?: FieldState<string> = undefined;

  /**
   * Additional Data
   */
  @observable propertyBeingTransacted?: FieldState<string> = undefined;
  @observable privacyPolicyField?: FieldState<boolean> = undefined;
  @observable transactionStatus?: FieldState<string> = undefined;

  //Used only for omniture
  @observable categorySelectField?: string = undefined;

  /**
   * We don't reverify this number
   */
  @observable verifiedPhoneNumber?: FieldState<string> = undefined;

  constructor() {
    makeObservable(this);
    this.reset();
  }

  @action finalise() {
        this.transactionStatus = new FieldState<string>({
      value: 'success',
      onUpdate: (transactionStatus) => this.transactionStatus = transactionStatus,
      validators: []
    });
  }

  @action reset() {
    this.orderId = null;
    this.transactionId = createGuid();
    this.firstName = new FieldState<string>({
      value: defaultValues.firstName,
      onUpdate: ((firstName) => this.firstName = firstName),
      validators: [validation.required, (val) => voiValidators.name(val, 'first name'), voiValidators.lessThan256]
    });
    this.middleNames = new FieldState<string>({
      value: defaultValues.middleNames,
      onUpdate: (middleNames) => this.middleNames = middleNames,
      validators: [voiValidators.lessThan256, (val) => voiValidators.name(val, 'middle name'),]
    });
    this.surname = new FieldState<string>({
      value: defaultValues.surname,
      onUpdate: (surname) => this.surname = surname,
      validators: [validation.required, (val) => voiValidators.name(val, 'surname'), voiValidators.lessThan256]
    });
    /**
     * Address
     */
    this.addressLookup = new FieldState({
      value: defaultValues.addressLookup,
      onUpdate: (addressLookup) => this.addressLookup = addressLookup,
      validators: [this.addressLookupRequired]
    });
    this.addressLine1 = new FieldState({
      value: defaultValues.addressLine1,
      onUpdate: (addressLine1) => this.addressLine1 = addressLine1,
      validators: [validation.required]
    });
    this.addressLine2 = new FieldState({
      value: defaultValues.addressLine2,
      onUpdate: (addressLine2) => this.addressLine2 = addressLine2,
      validators: []
    });
    this.suburb = new FieldState({
      value: defaultValues.suburb,
      onUpdate: (suburb) => this.suburb = suburb,
      validators: [validation.required, voiValidators.lessThan256]
    });
    this.state = new FieldState<DisplayValue>({
      value: defaultValues.state,
      onUpdate: (state) => this.state = state,
      validators: [(val) => validation.required(val.value)]
    });
    this.postcode = new FieldState({
      value: defaultValues.postcode,
      onUpdate: (postcode) => this.postcode = postcode,
      validators: [validation.required, voiValidators.australianPostCode]
    });
    this.dpid = new FieldState({
      value: defaultValues.dpid,
      onUpdate: (dpid) => this.dpid = dpid,
      validators: []
    });

    /**
     * Some general others
     */
    this.dob = new FieldState({
      value: defaultValues.dob,
      onUpdate: (dob) => this.dob = dob,
      validators: [
        validation.required,
        validation.dateOnly,
        (val) => {
          const {date} = validation.Dates.toDate(val);
          return validation.minDate(moment().subtract(100, 'year').toDate())(date);
        },
        (val) => {
          const {date} = validation.Dates.toDate(val);
          return validation.maxDate(moment().subtract(10, 'year').toDate())(date);
        },
      ]
    });
    this.email = new FieldState({
      value: defaultValues.email,
      onUpdate: (email) => this.email = email,
      validators: [validation.email]
    });

    /**
     * Phone verification
     */
    this.phoneNumber = new FieldState({
      value: defaultValues.phoneNumber,
      onUpdate: (phoneNumber) => this.phoneNumber = phoneNumber,
      validators: [validation.required, voiValidators.australianMobileNumber]
    });
    this.pinCode = new FieldState<string>({
      value: defaultValues.pinCode,
      onUpdate: (pinCode) => this.pinCode = pinCode,
      validators: [validation.required, voiValidators.pinCode]
    });

    /**
     * Additional Data
     */
    this.propertyBeingTransacted = new FieldState<string>({
      value: defaultValues.propertyBeingTransacted,
      onUpdate: (propertyBeingTransacted) => this.propertyBeingTransacted = propertyBeingTransacted,
      validators: [validation.required, voiValidators.address]
    });

    this.transactionStatus = new FieldState<string>({
      value: defaultValues.transactionStatus,
      onUpdate: (transactionStatus) => this.transactionStatus = transactionStatus,
      validators: []
    });

    this.verifiedPhoneNumber = new FieldState<string>({
      value: defaultValues.phoneNumber,
      onUpdate: (verifiedPhoneNumber) => this.verifiedPhoneNumber = verifiedPhoneNumber,
      validators: []
    });

    this.privacyPolicyField = new FieldState<boolean>({
      value: false,
      onUpdate: (privacyPolicyField) => this.privacyPolicyField = privacyPolicyField,
      validators: [(val) => {
        if (!val) return "You must accept to continue.";
      }]
    });

    this.searchTerm = '';
    this.isManualAddressEntry = false;
    this.useClientAddressAsTransacted = false;
    this.fullClientAddress = '';
  }

  addressLookupRequired: Validator<any> = (value: any) => {
    if (!this.isManualAddressEntry) {
      return validation.required(this.addressLine1.value);
    }

    return null;
}
  clearAddressLines() {
    this.addressLine1.setValue('');
    this.addressLine2.setValue('');
    this.suburb.setValue('');
    this.state.setValue({ display: '', value: '' });
    this.postcode.setValue('');
    this.dpid.setValue('');
    this.fullClientAddress = '';
  }
}
